import { Platform } from 'react-native';

import { URLS } from '@/data/config';
import { createActiveWalletJurisdictionHeaders } from '@/hooks/use-active-wallet';
import { hasUserPurchasedSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useQuery } from '@tanstack/react-query';

export type CoinPackagesType = {
    coin_package_id: number;
    coin_amount: number;
    cash_amount: number;
    real_money_price: number;
    expiry_in_hours: number | null;
    real_money_currency: string;
    is_active: boolean;
    is_ftd_only: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    external_product_id: string | null;
    image_url?: string;
    original_coin_amount?: number;
    original_cash_amount?: number;
    platform?: string;
};

type CoinPackagesResponse = {
    data: {
        coin_packages: CoinPackagesType[];
        in_app_packages: {
            GOOGLE: CoinPackagesType[];
            APPLE: CoinPackagesType[];
        };
    };
};

/**
 * Fetches the list of coin packages available
 */
const getCoinPackages = async (): Promise<CoinPackagesResponse> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/coin-packages`, {
        method: 'GET',
        headers: {
            ...createActiveWalletJurisdictionHeaders(),
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || 'Error fetching coin packages');
    }
    return data;
};

const getAllActiveProductIds = (arr: CoinPackagesType[]) =>
    arr
        .filter(item => item.is_active)
        .map(item => item.external_product_id)
        .filter((id): id is string => typeof id === 'string' && id !== '');

const filterCoinPackages = (item: CoinPackagesType) => item.is_active && !item.is_ftd_only;

const filterFtdCoinPackagesOnly = (item: CoinPackagesType) => item.is_active && item.is_ftd_only;

/**
 * Hook to fetch the list of removable debit cards
 * @param options - query options
 * @returns Query result for fetching removable debit cards
 */
export const useCoinPackages = () => {
    const { data: userHasPurchased } = useAuthUserConfig({
        select: hasUserPurchasedSelector,
    });
    const query = useQuery({
        queryKey: ['coin-packages'],
        queryFn: getCoinPackages,
        select: data => {
            const coin_packages = !userHasPurchased
                ? data.data.coin_packages.filter(item => filterFtdCoinPackagesOnly(item))
                : data.data.coin_packages.filter(item => filterCoinPackages(item));

            return {
                coin_packages,
                in_app_packages: Platform.select({
                    ios: data.data.in_app_packages.APPLE.filter(item => item.is_active),
                    android: data.data.in_app_packages.GOOGLE.filter(item => item.is_active),
                }),
                in_app_purchase_ids:
                    Platform.select({
                        ios: getAllActiveProductIds(data.data.in_app_packages.APPLE),
                        android: getAllActiveProductIds(data.data.in_app_packages.GOOGLE),
                    }) ?? [],
            };
        },
    });

    return query;
};
